<script setup lang="ts">
import useLaboratoryConsumerAnalysis, {
    LaboratoryConsumerAnalysisSearchParams
} from "@/laboratory/composables/useConsumerAnalysis";
import dayjs from "dayjs";
import {computed, ComputedRef, reactive, ref, watch} from "vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import Icon from "@/core/components/icon/Icon.vue";
import formatHelper from "@/core/helpers/formatHelper";
import useSwr from "@/core/composables/swr/useSwr";
import {creditApi, customerCertificationApi, notificationTemplateApi} from "@/container";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {CustomerCertification, CustomerSummary} from "@/customer/interfaces/Customer";
import {useToastStore} from "@/stores/toast.store";
import {storeToRefs} from "pinia";
import {useCreditStore} from "@/stores/credit.store";
import CreditStoreModal from "@/credit/components/CreditStoreModal.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Laboratory} from "@/core/interfaces/laboratory/Laboratory";
import LaboratoryNotificationSequencesCard
    from "@/laboratory/components/notification-sequence/LaboratoryNotificationSequencesCard.vue";
import ConsumerAnimationCell from "@/core/components/animation/ConsumerAnimationCell.vue";
import useNotificationSequenceIndex from "@/notification-sequences/composables/useNotificationSequenceIndex";

type DataTableItem = {
    name: string;
    birth_date: string | null;
    top_sale?: {
        datetime: string;
        product: {
            name: string;
            code: string;
        }
    };
    customer?: CustomerSummary;
    phone?: string;
}

const props = defineProps<{
    laboratoryId: string;
    laboratory: Laboratory;
}>();

let searchParams = reactive<LaboratoryConsumerAnalysisSearchParams>({
    size: 20,
    page: 1,
    since: dayjs().startOf("day").format("YYYY-MM-DD"),
    includes: ['top_sale', 'last_certification_notification'],
    sortBy: 'last_visit',
    order: 'desc'
});

const {credit} = storeToRefs(useCreditStore());
const creditModalVisible = ref(false);
const {data: templates, isLoading: isLoadingTemplates} = useSwr(
    `notification-templates?laboratory_id=${props.laboratoryId}`,
    () => notificationTemplateApi().index({laboratory_id: Number(props.laboratoryId)}),
    {
        revalidateOnFocus: false
    }
);
const {data: creditData, mutate: mutateCredits} = useSwr('credit', () => creditApi().read());
const {isLoading: loadingSequences} = useNotificationSequenceIndex({
    organization_id: props.laboratory.organization.id
});

watch(creditData, (newVal) => {
    if (newVal) {
        credit.value = newVal;
    }
});

const loadings = reactive<{
    [id: string]: boolean;
}>({});
const {data, isLoading, isValidating, mutate} = useLaboratoryConsumerAnalysis(
    props.laboratoryId,
    searchParams
);

const _data: ComputedRef<DataTableItem[] | undefined> = computed(() => {
    return data.value?.data.map((item) => {
        return {
            name: item.consumer?.information.name || "Patient Anonyme",
            birth_date: item.consumer?.information?.birth_date ?? null,
            top_sale: item.top_sale,
            customer: item.customer,
            phone: formatHelper.readablePhone(item.customer?.information.phone),
        };
    });
});

const headers = [
    {
        title: "Patient",
        key: "name",
        sortable: false
    },
    {
        title: "Achats",
        key: "top_sale",
        sortable: false
    },
    {
        title: 'Actions',
        key: 'actions',
        sortable: false
    }
];

/**
 * Compute the template to use
 */
const template = computed(() => {
    if (!templates.value?.length) {
        return null;
    }

    return templates.value[0];
});

/**
 * Handle the phone input change
 */
const handlePhoneInputChange = (value: string, item: DataTableItem) => {
    if (!data.value) {
        return;
    }

    data.value.data = data.value.data.map((_item) => {
        if (_item.customer?.id === item.customer?.id && _item.customer) {
            _item.customer.information.phone = formatHelper.readablePhone(value);
        }

        return _item;
    });
};

/**
 * Handle page update.
 *
 * @param page
 */
const handlePageUpdate = (page: number) => {
    searchParams.page = page;
};

/**
 * Handle item per page update.
 *
 * @param itemsPerPage
 */
const handleItemPerPageUpdate = (itemsPerPage: number) => {
    searchParams.size = itemsPerPage;
    searchParams.page = 1;
};

/**
 * If organization has enough credit, certify and notify the customer item.
 *
 * @param item
 */
const certifyAndNotify = async (item: DataTableItem) => {
    if (!item.customer) {
        return;
    }

    if (!hasEnoughCredit.value) {
        creditModalVisible.value = true;
        return;
    }

    loadings[item.customer.id] = true;
    certify(item)
        .then((certification) => {
            if (certification) {
                return notifyCertification(certification)
                    .then(() => {
                        const toast = useToastStore();
                        toast.showSuccess({content: 'Le message a bien été envoyé'});

                        mutate();
                        mutateCredits();
                    })
                    .catch((error) => {
                        if (error.response.status === 402) {
                            creditModalVisible.value = true;
                        } else {
                            const toast = useToastStore();
                            toast.showError({content: 'Une erreur est survenue lors de l\'envoi du message'});
                        }
                    });
            }
        })
        .catch(() => {
            const toast = useToastStore();
            toast.showError({content: 'Une erreur est survenue lors de la certification du patient'});
        })
        .finally(() => {
            if (item.customer) {
                loadings[item.customer.id] = false;
            }
        });
};

/**
 * Certify the customer item.
 *
 * @param item
 */
const certify = async (item: DataTableItem): Promise<CustomerCertification | null> => {
    if (!item.customer || !item.phone) {
        return Promise.reject("Phone number is required");
    }

    return await customerCertificationApi()
        .certifyPhone(item.customer.id, formatHelper.apiPhone(item.phone));
};

/**
 * Notify the customer item certification.
 *
 * @param {CustomerCertification} certification
 */
const notifyCertification = async (certification: CustomerCertification) => {
    if (!template.value) {
        return;
    }

    return await customerCertificationApi().createNotification(
        certification.id,
        {notification_template_id: template.value.id}
    );
};

/**
 * Determine whether the customer phone is valid
 *
 * @param phone
 */
const phoneIsValid = (phone?: string) => {
    if (!phone) {
        return false;
    }

    return formatHelper.phoneIsValid(phone);
};

const hasEnoughCredit = computed(() => {
    return (credit.value?.balance ?? 0) >= (template.value?.credits ?? 0);
});
</script>

<template>
  <div>
    <div class="p-6">
      <BaseFlexSpinner v-if="isLoadingTemplates || isLoading || loadingSequences" />
      <div v-else>
        <BaseCard>
          <BaseCardBody class="space-y-8">
            <div>
              <h1 class="text-black">
                Animez vos patients avec des SMS
              </h1>
              <p>
                {{ laboratory?.name }} vous aide à prendre soin de vos patients en fonction de <span class="underline">leur profil</span>
              </p>
            </div>
            <LaboratoryNotificationSequencesCard class="rounded-lg shadow-lg" :laboratory="laboratory" />
            <div>
              <div class="flex justify-between gap-4">
                <div>
                  <h2 class="text-black">
                    Patients du jour
                  </h2>
                  <p>
                    Envoyez un SMS à vos patients récents pour qu'ils retrouvent leur
                    ordonnance et tout l'espace {{ laboratory.name }} dans leur Application Apodis !
                  </p>
                </div>
                <p class="font-bold">
                  <Icon v-if="!hasEnoughCredit" name="mdi-alert-circle" color="red" />
                  <Icon v-else name="mdi-star" color="primary" />
                  Crédits restants : {{ credit?.balance ?? 0 }}
                </p>
              </div>
              <v-data-table-server
                :items="_data"
                :headers="headers"
                :items-length="data?.meta.total_consumer_count ?? 0"
                :loading="isLoading || isValidating"
                :items-per-page-options="[10, 20, 30]"
                :items-per-page="searchParams.size"
                :page="searchParams.page"
                @update:items-per-page="handleItemPerPageUpdate"
                @update:page="handlePageUpdate"
              >
                <template #[`item.name`]="{item}">
                  <ConsumerAnimationCell
                    :name="item.name"
                    :birth-date="item.birth_date"
                    :phone="item.phone"
                    :editable="!!item.customer?.information"
                    :onboarded="item.customer?.onboarded"
                    @input="(value) => handlePhoneInputChange(value, item)"
                  />
                </template>
                <template #[`item.top_sale`]="{item}">
                  <div v-if="item.top_sale" class="flex items-center gap-2">
                    <Icon name="mdi-cart-outline" :size="20" />
                    <p>{{ item.top_sale.product.name }} le {{ $filters.date(item.top_sale.datetime) }}</p>
                  </div>
                </template>
                <template #[`item.actions`]="{item}">
                  <div v-if="item.customer?.onboarded">
                    <p>
                      Notification programmée
                      <v-tooltip>
                        <template #activator="{props: activatorProps}">
                          <Icon name="mdi-clock" v-bind="activatorProps" color="primary" />
                        </template>
                        <div>
                          <p>
                            La notification suivante sera envoyée après chaque achat s'il dispose de l'application
                            Apodis :
                          </p>
                          <p>Merci de votre visite !</p>
                          <p>Retrouvez vos ordonnances et vos traitements sur l’application Apodis.</p>
                        </div>
                      </v-tooltip>
                    </p>
                  </div>
                  <div v-else-if="template">
                    <p v-if="!item.customer?.information.phone" class="font-bold">
                      Renseignez le numéro
                    </p>
                    <div v-else class="py-2">
                      <div class="space-y-2">
                        <div class="flex items-center gap-3">
                          <v-tooltip>
                            <template #activator="{props}">
                              <BaseButton
                                v-bind="props"
                                primary
                                :disabled="!phoneIsValid(item.phone)"
                                :loading="loadings[item.customer?.id]"
                                density="comfortable"
                                append-icon="mdi-send"
                                @click="certifyAndNotify(item)"
                              >
                                Envoyer
                              </BaseButton>
                            </template>
                            <div class="max-w-60">
                              <p v-if="template" class="font-bold">
                                {{ template.credits }} {{ $filters.pluralize('crédit', template.credits) }} SMS
                              </p>
                              {{ template?.parsed_content }}
                            </div>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                    <p v-if="item.customer?.latest_conversation_message" class="text-sm mb-1">
                      Dernier SMS
                      {{ $filters.date(item.customer.latest_conversation_message.created_at, 'relative') }}
                    </p>
                  </div>
                </template>
              </v-data-table-server>
            </div>
          </BaseCardBody>
        </BaseCard>
        <CreditStoreModal v-if="creditModalVisible" @close="creditModalVisible = false" />
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
